import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { rupiah, fmtDateTime, ROLE_LABELS } from "../lib/format";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  TrendingUp, ShoppingBag, Package, AlertTriangle, Wallet, Coins, ArrowDownCircle,
} from "lucide-react";

function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-medium text-slate-500">{label}</div>
            <div className="text-2xl font-bold font-display mt-1 text-slate-900 mono">{value}</div>
            {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
          </div>
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [low, setLow] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/items/low-stock").then((r) => setLow(r.data));
  }, []);

  const recent = stats?.recent_sales || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">Ringkasan operasional hari ini · {ROLE_LABELS[user.role]}</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={TrendingUp} label="Penjualan Hari Ini" value={rupiah(stats.total_sales_today)}
            sub={`${stats.transactions_today} transaksi`} color="bg-emerald-500" />
          {stats.profit_today !== undefined && (
            <KpiCard icon={Coins} label="Estimasi Laba" value={rupiah(stats.profit_today)} color="bg-orange-500" />
          )}
          <KpiCard icon={Wallet} label="Saldo Awal" value={rupiah(stats.opening_balance)} color="bg-slate-800" />
          <KpiCard icon={ArrowDownCircle} label="Pengeluaran Hari Ini" value={rupiah(stats.expense_today)} color="bg-rose-500" />
          <KpiCard icon={Package} label="Total Barang" value={stats.total_items} color="bg-blue-600" />
          <KpiCard icon={AlertTriangle} label="Stok Menipis" value={stats.low_stock_count}
            sub="di bawah minimum" color="bg-amber-500" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-4 w-4 text-rose-500" />
              <h3 className="font-semibold text-slate-900">Peringatan Stok Minimum</h3>
            </div>
            {low.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">Semua stok aman 👍</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {low.map((i) => (
                  <div key={i.id} className="flex items-center justify-between p-2.5 rounded-lg bg-rose-50 border border-rose-100">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{i.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{i.barcode}</div>
                    </div>
                    <Badge className="bg-rose-500 hover:bg-rose-500">{i.stock} {i.unit} / min {i.min_stock}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag className="h-4 w-4 text-emerald-500" />
              <h3 className="font-semibold text-slate-900">Transaksi Terakhir</h3>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">Belum ada transaksi</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {recent.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                    <div>
                      {s.number && <div className="text-sm font-medium text-slate-800 font-mono">{s.number}</div>}
                      <div className="text-xs text-slate-500">{fmtDateTime(s.created_at)} · {s.cashier}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-emerald-600 mono">{rupiah(s.total)}</div>
                      <div className="text-xs text-slate-400 uppercase">{s.payment_method}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
